import { useParams, useNavigate } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { FAQ_SECTIONS } from "@/components/mapping/mappingHelpContent";
import { useForceLightTheme } from "@/hooks/useForceLightTheme";

export function MappingHelpPage() {
  useForceLightTheme();
  const params = useParams({ strict: false }) as { token?: string };
  const token = (params.token ?? "") as string;
  const navigate = useNavigate();

  const handleBack = () => {
    if (window.history.length > 1) {
      window.history.back();
      return;
    }
    navigate({ to: "/mapping/$token", params: { token } });
  };

  return (
    <div className="flex h-[100dvh] flex-col bg-background">
      <header className="flex shrink-0 items-center gap-2 border-b px-2 py-2">
        <Button
          variant="ghost"
          size="icon"
          className="h-11 w-11"
          onClick={handleBack}
          aria-label="Back"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-base font-semibold">How it works</h1>
          <p className="truncate text-xs text-muted-foreground">
            Guide for field mappers
          </p>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto">
        <div className="space-y-2 px-4 py-5">
          <p className="text-sm text-muted-foreground">
            Tap a question to expand the answer. If you need a refresher on a
            specific form field, tap the info icon next to the field label on
            any mapping page.
          </p>
        </div>

        {FAQ_SECTIONS.map((section) => (
          <section key={section.title} className="border-t px-4 py-4">
            <h2 className="mb-1 text-sm font-extrabold uppercase tracking-wide text-primary">
              {section.title}
            </h2>
            <Accordion type="multiple" className="w-full">
              {section.items.map((item, idx) => (
                <AccordionItem key={idx} value={`${section.title}-${idx}`}>
                  <AccordionTrigger className="text-left text-sm">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent>
                    <p className="whitespace-pre-line text-sm leading-relaxed text-foreground">
                      {item.a}
                    </p>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>
        ))}

        <div className="px-4 py-6 text-center text-xs text-muted-foreground">
          Still stuck? Contact your mapping coordinator.
        </div>
      </div>
    </div>
  );
}
